import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, CircularProgress, Dialog, Slider, Switch } from "@mui/material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CropIcon from "@mui/icons-material/Crop";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FlipIcon from "@mui/icons-material/Flip";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import FlipToBackOutlinedIcon from "@mui/icons-material/FlipToBackOutlined";
import FlipToFrontOutlinedIcon from "@mui/icons-material/FlipToFrontOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import RedoIcon from "@mui/icons-material/Redo";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { useIsDarkMode } from "../../../theme/ThemeContext";
import {
  fetchSignageAsset,
  fetchSignageAssetBlobUrl,
  fetchSignageAssetSourceBlobUrl,
  fetchSignageQrBlobUrl,
  formatBytes,
  replaceSignageAssetContent,
  uploadSignageAsset,
} from "../signageApi";
import { ErrorNote, INPUT, SEGMENT_TRACK, segmentClass } from "../signageUi";
import { Spinner } from "../../../components/UI/States";
import {
  CANVAS_PRESETS,
  croppedSize,
  defaultProject,
  fitRect,
  flipCrop,
  layerBounds,
  newLayerId,
  newShapeLayer,
  newTextLayer,
  placeImage,
  renderProject,
  rotateCrop,
  sizeForPreset,
  uncroppedRect,
} from "./render";

const IMAGE_FILES = ".jpg,.jpeg,.png,.gif,.bmp,.webp";

// QR code layers are image layers whose picture the server generates
// (GET /api/signage/qr). The settings live on the layer (`qr`) and are also
// packed into its `src`, so reopening a saved project regenerates the code.
const QR_DEFAULTS = { data: "", dark: "#000000", light: "#ffffff", margin: 2 };
const qrSrcOf = (qr) => `qr:${JSON.stringify([qr.data, qr.dark, qr.light, qr.margin])}`;
const qrFromSrc = (src) => {
  const [data, dark, light, margin] = JSON.parse(src.slice(3));
  return { data, dark, light, margin };
};
// "bigclumber.com/spring" → "https://bigclumber.com/spring"; anything with a
// scheme (https:, mailto:, tel:) or with spaces is kept as typed.
const normalizeQrData = (text) => {
  const t = String(text || "").trim();
  if (!t || /^[a-z][a-z0-9+.-]*:/i.test(t) || /\s/.test(t) || !t.includes(".")) return t;
  return `https://${t}`;
};

// Signage image editor: fix up a library image for the TVs — put it on a
// 1920×1080 (or portrait) screen, fill the leftover space with a blurred
// copy / color / gradient, add text, banners, and logos, adjust the photo,
// and save as a new copy or in place of the original. The layered project
// is saved with the image, so text stays editable next time.

const env = {
  createCanvas: (w, h) => {
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    return c;
  },
};

// Fonts offered for text. The Google ones load on demand the first time the
// editor opens (the canvas can only draw a font once the browser has it).
const FONTS = [
  { family: "Montserrat", google: true },
  { family: "Oswald", google: true },
  { family: "Bebas Neue", google: true },
  { family: "Roboto", google: true },
  { family: "Playfair Display", google: true },
  { family: "Arial" },
  { family: "Georgia" },
  { family: "Impact" },
  { family: "Verdana" },
];
const GOOGLE_FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,400;0,700;1,400;1,700&family=Oswald:wght@400;700&family=Bebas+Neue&family=Roboto:ital,wght@0,400;0,700;1,400;1,700&family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&display=swap";

const ensureFontStylesheet = () => {
  if (document.getElementById("signage-editor-fonts")) return;
  const link = document.createElement("link");
  link.id = "signage-editor-fonts";
  link.rel = "stylesheet";
  link.href = GOOGLE_FONTS_URL;
  document.head.appendChild(link);
};

const loadFontsFor = (project) =>
  Promise.all(
    (project.layers || [])
      .filter((l) => l.type === "text")
      .flatMap((l) => [
        document.fonts?.load(`${l.italic ? "italic " : ""}${l.bold ? 700 : 400} 40px "${l.fontFamily}"`),
      ])
  ).catch(() => {});

const loadImage = async (url) => {
  const img = new Image();
  img.src = url;
  await img.decode();
  return img;
};

const HANDLE_PX = 10; // on-screen handle size
const SNAP_PX = 8; // on-screen snap distance
const HISTORY_LIMIT = 60;

const clone = (v) => JSON.parse(JSON.stringify(v));
const stemOf = (filename) => filename.replace(/\.[^.]+$/, "");

// ================= small UI pieces =================

const Section = ({ title, children, aside }) => (
  <section className="app-divider space-y-3 border-b px-4 py-4">
    <div className="flex items-center justify-between">
      <h4 className="app-eyebrow">{title}</h4>
      {aside}
    </div>
    {children}
  </section>
);

const Field = ({ label, children, value }) => (
  <label className="block">
    <span className="app-muted flex justify-between text-xs font-medium">
      <span>{label}</span>
      {value != null && <span className="tabular-nums">{value}</span>}
    </span>
    {children}
  </label>
);

const Segmented = ({ options, value, onChange }) => (
  <div className={`flex w-full ${SEGMENT_TRACK}`}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        title={o.title || o.label}
        aria-pressed={value === o.value}
        onClick={() => onChange(o.value)}
        className={`${segmentClass(value === o.value)} flex-1 !px-2`}
      >
        {o.icon}
        {o.label}
      </button>
    ))}
  </div>
);

const IconButton = ({ title, onClick, disabled, children, active }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    aria-pressed={active === undefined ? undefined : !!active}
    onClick={onClick}
    disabled={disabled}
    className={`flex h-8 min-w-[2rem] items-center justify-center rounded-lg border px-1.5 text-sm transition disabled:cursor-not-allowed disabled:opacity-40 ${
      active
        ? "border-sky-500/50 bg-sky-500/15 text-sky-700 dark:text-sky-300"
        : "app-input hover:border-sky-400"
    }`}
  >
    {children}
  </button>
);

const ColorInput = ({ value, onChange, allowNone, noneLabel = "None" }) => (
  <div className="flex items-center gap-2">
    <input
      type="color"
      value={value || "#000000"}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 w-12 cursor-pointer rounded-lg border border-[color:var(--app-input-border)] bg-transparent p-0.5"
    />
    <span className="app-muted font-mono text-xs">{value || noneLabel}</span>
    {allowNone && value && (
      <button
        type="button"
        className="app-muted ml-auto text-xs font-medium hover:text-rose-600 dark:hover:text-rose-400"
        onClick={() => onChange("")}
      >
        remove
      </button>
    )}
  </div>
);

const RangeField = ({ label, value, min, max, step = 1, onChange, format }) => (
  <Field label={label} value={format ? format(value) : value}>
    <Slider
      size="small"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(_e, v) => onChange(v)}
    />
  </Field>
);

// ================= editor =================

const SignageImageEditor = ({
  asset,
  scope,
  libraryImages,
  userId,
  onClose,
  onSaved,
  onLibraryChanged,
}) => {
  const isDark = useIsDarkMode();
  const [project, setProject] = useState(null);
  const [images, setImages] = useState({});
  const [sourceInfo, setSourceInfo] = useState(null); // { width, height }
  const [selectedId, setSelectedId] = useState(null);
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const [error, setError] = useState(null);
  const [fontsTick, setFontsTick] = useState(0);
  const [guides, setGuides] = useState({ v: false, h: false });
  // Zoom: "fit" follows the window; a number is a fixed scale (1 = 100%).
  const [fitScale, setFitScale] = useState(0.4);
  const [zoom, setZoom] = useState("fit");
  const scale = zoom === "fit" ? fitScale : zoom;
  const [showSave, setShowSave] = useState(false);
  const [showLogoPicker, setShowLogoPicker] = useState(false);
  const [qrDialog, setQrDialog] = useState(null); // null | { layerId?, qr }
  const [uploadedImages, setUploadedImages] = useState([]); // added from the picker
  // Crop tool: { layerId, rect, full, aspect } — rect is the crop box and
  // full the whole uncropped image, both in canvas pixels.
  const [cropping, setCropping] = useState(null);

  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const drag = useRef(null);
  const coalesce = useRef({ key: null, at: 0 });
  const objectUrls = useRef([]);
  const projectRef = useRef(null);
  projectRef.current = project;

  // ---- load ----
  useEffect(() => {
    let cancelled = false;
    ensureFontStylesheet();
    (async () => {
      try {
        const [meta, sourceUrl] = await Promise.all([
          fetchSignageAsset(asset.id, userId),
          fetchSignageAssetSourceBlobUrl(asset.id, userId),
        ]);
        objectUrls.current.push(sourceUrl);
        const source = await loadImage(sourceUrl);
        const loaded = { source };
        const initial = meta.project || defaultProject(source);
        // Other library images the project uses (logos, background).
        const refs = new Set(
          [...initial.layers.map((l) => l.src), initial.background?.src].filter(
            (src) => src && (src.startsWith("asset:") || src.startsWith("qr:"))
          )
        );
        await Promise.all(
          [...refs].map(async (src) => {
            try {
              const url = src.startsWith("qr:")
                ? await fetchSignageQrBlobUrl(qrFromSrc(src), userId)
                : await fetchSignageAssetBlobUrl(src.slice(6), userId);
              objectUrls.current.push(url);
              loaded[src] = await loadImage(url);
            } catch {
              /* that image was deleted — its layer simply won't draw */
            }
          })
        );
        if (cancelled) return;
        setImages(loaded);
        setSourceInfo({ width: source.width, height: source.height });
        setProject(initial);
        loadFontsFor(initial).then(() => !cancelled && setFontsTick((t) => t + 1));
        document.fonts?.ready.then(() => !cancelled && setFontsTick((t) => t + 1));
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    const urls = objectUrls.current;
    return () => {
      cancelled = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [asset.id, userId]);

  // ---- history ----
  // commit(): record the current state, then apply `next`. Repeated edits of
  // the same control within a moment (a slider drag, typing) coalesce into
  // one undo step.
  const commit = useCallback((next, coalesceKey) => {
    const now = Date.now();
    const merge =
      coalesceKey &&
      coalesce.current.key === coalesceKey &&
      now - coalesce.current.at < 1000;
    coalesce.current = { key: coalesceKey || null, at: now };
    if (!merge) {
      setPast((p) => [...p.slice(-HISTORY_LIMIT + 1), projectRef.current]);
      setFuture([]);
    }
    setProject(next);
  }, []);

  const undo = useCallback(() => {
    if (!past.length) return;
    setFuture([project, ...future]);
    setProject(past[past.length - 1]);
    setPast(past.slice(0, -1));
    coalesce.current = { key: null, at: 0 };
  }, [past, future, project]);

  const redo = useCallback(() => {
    if (!future.length) return;
    setPast([...past, project]);
    setProject(future[0]);
    setFuture(future.slice(1));
    coalesce.current = { key: null, at: 0 };
  }, [past, future, project]);

  const selected = project?.layers.find((l) => l.id === selectedId) || null;

  const updateLayer = (id, patch, coalesceKey) =>
    commit(
      {
        ...project,
        layers: project.layers.map((l) => (l.id === id ? { ...l, ...patch } : l)),
      },
      coalesceKey
    );
  const updateSelected = (patch, field) =>
    selected && updateLayer(selected.id, patch, field ? `${selected.id}:${field}` : null);
  const updateBackground = (patch, field) =>
    commit(
      { ...project, background: { ...project.background, ...patch } },
      field ? `bg:${field}` : null
    );

  const addLayer = (layer) => {
    commit({ ...project, layers: [...project.layers, layer] });
    setSelectedId(layer.id);
  };

  const removeSelected = useCallback(() => {
    const p = projectRef.current;
    if (!p || !selectedId) return;
    commit({ ...p, layers: p.layers.filter((l) => l.id !== selectedId) });
    setSelectedId(null);
  }, [commit, selectedId]);

  const duplicateSelected = useCallback(() => {
    const p = projectRef.current;
    const layer = p?.layers.find((l) => l.id === selectedId);
    if (!layer) return;
    const copy = { ...clone(layer), id: newLayerId(), x: layer.x + 30, y: layer.y + 30 };
    const idx = p.layers.indexOf(layer);
    const layers = [...p.layers];
    layers.splice(idx + 1, 0, copy);
    commit({ ...p, layers });
    setSelectedId(copy.id);
  }, [commit, selectedId]);

  const moveLayer = (dir) => {
    const idx = project.layers.findIndex((l) => l.id === selectedId);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= project.layers.length) return;
    const layers = [...project.layers];
    [layers[idx], layers[j]] = [layers[j], layers[idx]];
    commit({ ...project, layers });
  };

  // ---- stage sizing ----
  const projectWidth = project?.width;
  const projectHeight = project?.height;
  useEffect(() => {
    const el = stageRef.current;
    if (!el || !projectWidth) return;
    const fit = () => {
      const pad = 48;
      const s = Math.min(
        (el.clientWidth - pad) / projectWidth,
        (el.clientHeight - pad) / projectHeight
      );
      setFitScale(Math.max(0.05, s));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [projectWidth, projectHeight]);

  const ZOOM_STEPS = [0.1, 0.15, 0.25, 0.33, 0.5, 0.67, 0.75, 1, 1.25, 1.5, 2, 3];
  const zoomBy = (dir) => {
    const current = zoom === "fit" ? fitScale : zoom;
    const next =
      dir > 0
        ? ZOOM_STEPS.find((z) => z > current + 0.001)
        : [...ZOOM_STEPS].reverse().find((z) => z < current - 0.001);
    if (next) setZoom(next);
  };
  const zoomByRef = useRef(zoomBy);
  zoomByRef.current = zoomBy;

  // Where the selected layer sits on screen, for the floating toolbar.
  const selectionBox =
    selected && canvasRef.current
      ? (() => {
          const b = layerBounds(canvasRef.current.getContext("2d"), selected);
          return { x: b.x * scale, y: b.y * scale, w: b.w * scale, h: b.h * scale };
        })()
      : null;

  // ---- drawing ----
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !project) return;
    const frame = requestAnimationFrame(() => {
      const ctx = canvas.getContext("2d");
      const px = 1 / scale;

      if (cropping) {
        // Show the whole image (dimmed outside the crop box) so there's
        // something to pull the edges out to.
        const F = cropping.full;
        const R = cropping.rect;
        renderProject(
          ctx,
          {
            ...project,
            layers: project.layers.map((l) =>
              l.id === cropping.layerId
                ? { ...l, crop: undefined, radius: 0, opacity: 1, x: F.x, y: F.y, w: F.w, h: F.h }
                : l
            ),
          },
          images,
          env
        );
        ctx.save();
        ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
        ctx.beginPath();
        ctx.rect(0, 0, project.width, project.height);
        ctx.rect(R.x, R.y, R.w, R.h);
        ctx.fill("evenodd");
        ctx.strokeStyle = "rgba(255, 255, 255, 0.45)";
        ctx.lineWidth = 1 * px;
        ctx.beginPath();
        for (const t of [1 / 3, 2 / 3]) {
          ctx.moveTo(R.x + R.w * t, R.y);
          ctx.lineTo(R.x + R.w * t, R.y + R.h);
          ctx.moveTo(R.x, R.y + R.h * t);
          ctx.lineTo(R.x + R.w, R.y + R.h * t);
        }
        ctx.stroke();
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 2 * px;
        ctx.strokeRect(R.x, R.y, R.w, R.h);
        ctx.fillStyle = "#ffffff";
        ctx.strokeStyle = "#0ea5e9";
        for (const h of cropHandles(R, cropping.aspect)) {
          const s = HANDLE_PX * 1.2 * px;
          ctx.fillRect(h.x - s / 2, h.y - s / 2, s, s);
          ctx.strokeRect(h.x - s / 2, h.y - s / 2, s, s);
        }
        ctx.restore();
        return;
      }

      renderProject(ctx, project, images, env);

      // Selection outline + handles, sized for the screen, not the canvas.
      if (selected) {
        const b = layerBounds(ctx, selected);
        ctx.save();
        ctx.strokeStyle = "#0ea5e9";
        ctx.lineWidth = 2 * px;
        ctx.setLineDash([6 * px, 4 * px]);
        ctx.strokeRect(b.x, b.y, b.w, b.h);
        ctx.setLineDash([]);
        ctx.fillStyle = "#ffffff";
        for (const h of handlePoints(selected, b)) {
          const s = HANDLE_PX * px;
          ctx.fillRect(h.x - s / 2, h.y - s / 2, s, s);
          ctx.strokeRect(h.x - s / 2, h.y - s / 2, s, s);
        }
        ctx.restore();
      }
      if (guides.v || guides.h) {
        ctx.save();
        ctx.strokeStyle = "#f43f5e";
        ctx.lineWidth = 1.5 * px;
        ctx.beginPath();
        if (guides.v) {
          ctx.moveTo(project.width / 2, 0);
          ctx.lineTo(project.width / 2, project.height);
        }
        if (guides.h) {
          ctx.moveTo(0, project.height / 2);
          ctx.lineTo(project.width, project.height / 2);
        }
        ctx.stroke();
        ctx.restore();
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [project, images, selected, scale, guides, fontsTick, cropping]);

  // ---- crop tool ----
  const startCrop = (layer = selected) => {
    if (!layer || layer.type !== "image" || !images[layer.src]) return;
    setSelectedId(layer.id);
    setCropping({
      layerId: layer.id,
      full: uncroppedRect(layer),
      rect: { x: layer.x, y: layer.y, w: layer.w, h: layer.h },
      aspect: null,
    });
  };

  // Largest box of `aspect` (w/h) around the current crop's center that
  // still fits on the image.
  const setCropAspect = (aspect) => {
    setCropping((c) => {
      if (!aspect) return { ...c, aspect: null };
      const F = c.full;
      const fit = fitRect({ w: aspect, h: 1 }, { w: F.w, h: F.h });
      const cx = c.rect.x + c.rect.w / 2;
      const cy = c.rect.y + c.rect.h / 2;
      const x = Math.min(Math.max(cx - fit.w / 2, F.x), F.x + F.w - fit.w);
      const y = Math.min(Math.max(cy - fit.h / 2, F.y), F.y + F.h - fit.h);
      return { ...c, aspect, rect: { x, y, w: fit.w, h: fit.h } };
    });
  };

  const applyCropRef = useRef(null);
  const applyCrop = () => {
    const c = cropping;
    const layer = project.layers.find((l) => l.id === c.layerId);
    setCropping(null);
    if (!layer) return;
    const F = c.full;
    const R = c.rect;
    const crop = {
      x: (R.x - F.x) / F.w,
      y: (R.y - F.y) / F.h,
      w: R.w / F.w,
      h: R.h / F.h,
    };
    const whole =
      crop.x < 0.001 && crop.y < 0.001 && crop.w > 0.999 && crop.h > 0.999;
    updateLayer(layer.id, { ...R, crop: whole ? undefined : crop });
  };
  applyCropRef.current = applyCrop;

  // ---- pointer interaction ----
  const toCanvas = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * project.width,
      y: ((e.clientY - rect.top) / rect.height) * project.height,
    };
  };

  const hitHandle = (pt, layer, ctx) => {
    if (!layer) return null;
    const b = layerBounds(ctx, layer);
    const r = (HANDLE_PX / scale) * 0.9;
    return (
      handlePoints(layer, b).find(
        (h) => Math.abs(pt.x - h.x) <= r && Math.abs(pt.y - h.y) <= r
      ) || null
    );
  };

  const hitLayer = (pt, ctx) => {
    for (let i = project.layers.length - 1; i >= 0; i--) {
      const l = project.layers[i];
      if (l.hidden) continue;
      const b = layerBounds(ctx, l);
      if (pt.x >= b.x && pt.x <= b.x + b.w && pt.y >= b.y && pt.y <= b.y + b.h) return l;
    }
    return null;
  };

  const onPointerDown = (e) => {
    if (!project) return;
    const pt = toCanvas(e);
    if (cropping) {
      const R = cropping.rect;
      const r = (HANDLE_PX / scale) * 1.2;
      const handle = cropHandles(R, cropping.aspect).find(
        (h) => Math.abs(pt.x - h.x) <= r && Math.abs(pt.y - h.y) <= r
      );
      const inside = pt.x >= R.x && pt.x <= R.x + R.w && pt.y >= R.y && pt.y <= R.y + R.h;
      if (!handle && !inside) return;
      canvasRef.current.setPointerCapture(e.pointerId);
      drag.current = { mode: handle ? "crop-resize" : "crop-move", handle: handle?.key, start: pt, rect: { ...R } };
      return;
    }
    const ctx = canvasRef.current.getContext("2d");
    const handle = hitHandle(pt, selected, ctx);
    const layer = handle ? selected : hitLayer(pt, ctx);
    setSelectedId(layer?.id || null);
    if (!layer) return;
    canvasRef.current.setPointerCapture(e.pointerId);
    drag.current = {
      mode: handle ? "resize" : "move",
      handle: handle?.key,
      start: pt,
      layer: clone(layer),
      bounds: layerBounds(ctx, layer),
      before: project,
      moved: false,
    };
  };

  const onPointerMove = (e) => {
    const canvas = canvasRef.current;
    if (!project || !canvas) return;
    const pt = toCanvas(e);
    const d = drag.current;
    if (cropping) {
      const R0 = d?.rect || cropping.rect;
      if (!d) {
        const r = (HANDLE_PX / scale) * 1.2;
        const h = cropHandles(R0, cropping.aspect).find(
          (hh) => Math.abs(pt.x - hh.x) <= r && Math.abs(pt.y - hh.y) <= r
        );
        const inside = pt.x >= R0.x && pt.x <= R0.x + R0.w && pt.y >= R0.y && pt.y <= R0.y + R0.h;
        canvas.style.cursor = h ? h.cursor : inside ? "move" : "default";
        return;
      }
      setCropping((c) => ({
        ...c,
        rect: dragCropRect(d, pt, c.full, c.aspect, 24 / scale),
      }));
      return;
    }
    if (!d) {
      // Hover cursor.
      const ctx = canvas.getContext("2d");
      const h = hitHandle(pt, selected, ctx);
      canvas.style.cursor = h
        ? h.cursor
        : hitLayer(pt, ctx)
          ? "move"
          : "default";
      return;
    }
    d.moved = true;
    const dx = pt.x - d.start.x;
    const dy = pt.y - d.start.y;
    const L = d.layer;
    let patch;

    if (d.mode === "move") {
      let x = L.x + dx;
      let y = L.y + dy;
      const snap = SNAP_PX / scale;
      const g = { v: false, h: false };
      const cx = x + d.bounds.w / 2;
      const cy = y + d.bounds.h / 2;
      if (Math.abs(cx - project.width / 2) < snap) {
        x = project.width / 2 - d.bounds.w / 2;
        g.v = true;
      } else if (Math.abs(x) < snap) x = 0;
      else if (Math.abs(x + d.bounds.w - project.width) < snap) x = project.width - d.bounds.w;
      if (Math.abs(cy - project.height / 2) < snap) {
        y = project.height / 2 - d.bounds.h / 2;
        g.h = true;
      } else if (Math.abs(y) < snap) y = 0;
      else if (Math.abs(y + d.bounds.h - project.height) < snap) y = project.height - d.bounds.h;
      setGuides(g);
      patch = { x, y };
    } else if (d.handle === "e") {
      patch = { w: Math.max(40, L.w + dx) };
    } else {
      const B = d.bounds;
      const west = d.handle.includes("w");
      const north = d.handle.includes("n");
      const ax = west ? B.x + B.w : B.x;
      const ay = north ? B.y + B.h : B.y;
      if (L.type === "shape" && !e.shiftKey) {
        // Shapes stretch freely (hold Shift to keep proportions).
        patch = {
          x: Math.min(ax, pt.x),
          y: Math.min(ay, pt.y),
          w: Math.max(10, Math.abs(pt.x - ax)),
          h: Math.max(10, Math.abs(pt.y - ay)),
        };
      } else {
        const cornerX = west ? B.x : B.x + B.w;
        const cornerY = north ? B.y : B.y + B.h;
        const s = Math.max(
          0.05,
          Math.hypot(pt.x - ax, pt.y - ay) / Math.max(1, Math.hypot(cornerX - ax, cornerY - ay))
        );
        const w = B.w * s;
        const h = B.h * s;
        const x = west ? ax - w : ax;
        const y = north ? ay - h : ay;
        patch =
          L.type === "text"
            ? {
                x,
                y,
                w,
                fontSize: Math.max(6, L.fontSize * s),
                bgPadding: (L.bgPadding ?? 0) * s,
                bgRadius: (L.bgRadius ?? 0) * s,
                outlineWidth: (L.outlineWidth ?? 0) * s,
              }
            : { x, y, w, h };
      }
    }
    setProject((p) => ({
      ...p,
      layers: p.layers.map((l) => (l.id === L.id ? { ...l, ...patch } : l)),
    }));
  };

  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    setGuides({ v: false, h: false });
    if (d?.moved) {
      setPast((p) => [...p.slice(-HISTORY_LIMIT + 1), d.before]);
      setFuture([]);
      coalesce.current = { key: null, at: 0 };
    }
  };

  // ---- keyboard ----
  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName;
      const typing =
        ["INPUT", "TEXTAREA", "SELECT"].includes(tag) || e.target?.isContentEditable;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === "z") {
        if (typing) return;
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if (typing) return;
      if (mod && (e.key === "=" || e.key === "+" || e.key === "-" || e.key === "0")) {
        e.preventDefault();
        if (e.key === "0") setZoom("fit");
        else zoomByRef.current(e.key === "-" ? -1 : 1);
        return;
      }
      if (cropping) {
        if (e.key === "Enter") {
          e.preventDefault();
          applyCropRef.current();
        } else if (e.key === "Escape") {
          setCropping(null);
        }
        return;
      }
      if (mod && e.key.toLowerCase() === "d") {
        e.preventDefault();
        duplicateSelected();
      } else if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedId) {
          e.preventDefault();
          removeSelected();
        }
      } else if (e.key === "Escape") {
        setSelectedId(null);
      } else if (e.key.startsWith("Arrow") && selectedId) {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const p = projectRef.current;
        const layer = p.layers.find((l) => l.id === selectedId);
        if (!layer) return;
        const delta = {
          ArrowLeft: [-step, 0],
          ArrowRight: [step, 0],
          ArrowUp: [0, -step],
          ArrowDown: [0, step],
        }[e.key];
        commit(
          {
            ...p,
            layers: p.layers.map((l) =>
              l.id === selectedId ? { ...l, x: l.x + delta[0], y: l.y + delta[1] } : l
            ),
          },
          `${selectedId}:nudge`
        );
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId, undo, redo, removeSelected, duplicateSelected, commit, cropping]);

  // ---- canvas size ----
  const setPreset = (preset) => {
    const size = sizeForPreset(preset, images.source);
    const sx = size.width / project.width;
    const sy = size.height / project.height;
    const layers = project.layers.map((l) =>
      l.type === "image" && l.src === "source"
        ? placeImage(l, images.source, size, "contain")
        : { ...l, x: l.x * sx, y: l.y * sy }
    );
    commit({ ...project, ...size, preset, layers });
  };

  const fitSelected = (mode) => {
    const img = images[selected.src];
    if (img) updateSelected(placeImage(selected, img, project, mode));
  };

  // dir 1 = clockwise as seen. A mirrored image turns the other way in
  // rotate-value terms (mirroring reverses rotation), and the crop turns
  // with the picture.
  const rotateSelected = (dir) => {
    const img = images[selected.src];
    if (!img) return;
    const step = selected.flipX ? -dir : dir;
    const rotate = (((selected.rotate || 0) + step * 90) % 360 + 360) % 360;
    const crop = rotateCrop(selected.crop, dir);
    // Keep the same center and roughly the same size after a quarter turn.
    const cx = selected.x + selected.w / 2;
    const cy = selected.y + selected.h / 2;
    const drawn = croppedSize(img, { ...selected, rotate, crop });
    const side = Math.max(selected.w, selected.h);
    const r = fitRect(drawn, { w: side, h: side });
    updateSelected({ rotate, crop, w: r.w, h: r.h, x: cx - r.w / 2, y: cy - r.h / 2 });
  };

  const flipSelected = () =>
    updateSelected({ flipX: !selected.flipX, crop: flipCrop(selected.crop) });

  // Generate (or reuse) the picture for a QR code's settings.
  const loadQrImage = async (qr) => {
    const src = qrSrcOf(qr);
    if (images[src]) return { src, img: images[src] };
    const url = await fetchSignageQrBlobUrl(qr, userId);
    objectUrls.current.push(url);
    const img = await loadImage(url);
    setImages((m) => ({ ...m, [src]: img }));
    return { src, img };
  };

  // Add a new QR layer (bottom-right, big enough to scan from a distance),
  // or swap the code on an existing one in place.
  const saveQr = async (qr, layerId) => {
    const { src } = await loadQrImage(qr);
    setQrDialog(null);
    if (layerId) {
      updateLayer(layerId, { src, qr });
      return;
    }
    const size = Math.round(Math.min(project.width, project.height) * 0.3);
    const pad = Math.round(Math.min(project.width, project.height) * 0.04);
    addLayer({
      id: newLayerId(),
      type: "image",
      src,
      qr,
      x: project.width - size - pad,
      y: project.height - size - pad,
      w: size,
      h: size,
      rotate: 0,
      flipX: false,
      brightness: 100,
      contrast: 100,
      saturation: 100,
      opacity: 1,
      radius: 0,
    });
  };

  const addLogo = async (libAsset) => {
    setShowLogoPicker(false);
    try {
      const src = `asset:${libAsset.id}`;
      let img = images[src];
      if (!img) {
        const url = await fetchSignageAssetBlobUrl(libAsset.id, userId);
        objectUrls.current.push(url);
        img = await loadImage(url);
        setImages((m) => ({ ...m, [src]: img }));
      }
      const box = { w: project.width * 0.25, h: project.height * 0.25 };
      const r = fitRect({ w: img.width, h: img.height }, box);
      addLayer({
        id: newLayerId(),
        type: "image",
        src,
        x: project.width - r.w - project.width * 0.04,
        y: project.height * 0.05,
        w: r.w,
        h: r.h,
        rotate: 0,
        flipX: false,
        brightness: 100,
        contrast: 100,
        saturation: 100,
        opacity: 1,
        radius: 0,
      });
    } catch (err) {
      setError(err.message);
    }
  };

  const setBackgroundImage = async (libAsset) => {
    setShowLogoPicker(false);
    const src = `asset:${libAsset.id}`;
    try {
      if (!images[src]) {
        const url = await fetchSignageAssetBlobUrl(libAsset.id, userId);
        objectUrls.current.push(url);
        const img = await loadImage(url);
        setImages((m) => ({ ...m, [src]: img }));
      }
      updateBackground({ type: "image", src });
    } catch (err) {
      setError(err.message);
    }
  };

  const close = () => {
    if (past.length && !window.confirm("Close the editor? Your changes haven't been saved.")) {
      return;
    }
    onClose();
  };

  const layerLabel = (l) =>
    l.type === "text"
      ? `“${String(l.text).split("\n")[0].slice(0, 24) || "Text"}”`
      : l.type === "shape"
        ? l.shape === "ellipse"
          ? "Circle"
          : "Banner / box"
        : l.qr
          ? "QR code"
          : l.src === "source"
            ? "Photo"
            : "Logo / image";

  // Library images to pick logos/backgrounds from, plus any uploaded from
  // the picker during this session (the library list is refreshed too).
  const otherImages = [
    ...(libraryImages || []),
    ...uploadedImages.filter((u) => !(libraryImages || []).some((a) => a.id === u.id)),
  ].filter((a) => a.id !== asset.id);

  // ================= render =================
  return (
    <Dialog
      open
      fullScreen
      onClose={close}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          // A solid, neutral workspace (not glass) so the picture is judged
          // against a steady background; square edges for full screen.
          bgcolor: isDark ? "#0f172a" : "#f1f5f9",
          color: isDark ? "#f1f5f9" : "#1e293b",
          backgroundImage: "none",
          borderRadius: 0,
          border: 0,
        },
      }}
    >
      <div className="flex h-full flex-col">
        {/* Top bar */}
        <div className="app-card flex items-center gap-2 border-b px-2 py-2 shadow-none sm:px-3">
          <button
            type="button"
            className="app-icon-btn"
            title="Close"
            aria-label="Close editor"
            onClick={close}
          >
            <CloseIcon fontSize="small" />
          </button>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{asset.filename}</p>
            <p className="app-muted truncate text-xs">
              {project ? `${project.width}×${project.height} screen` : "Loading…"}
              {sourceInfo &&
                ` · original ${sourceInfo.width}×${sourceInfo.height}, ${formatBytes(asset.sizeBytes)}`}
            </p>
          </div>
          <div className="app-divider ml-2 flex gap-1 border-l pl-2 sm:ml-3 sm:pl-3">
            <IconButton title="Undo (Ctrl/⌘+Z)" onClick={undo} disabled={!past.length}>
              <UndoIcon fontSize="small" />
            </IconButton>
            <IconButton title="Redo (Shift+Ctrl/⌘+Z)" onClick={redo} disabled={!future.length}>
              <RedoIcon fontSize="small" />
            </IconButton>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {past.length > 0 && (
              <span className="hidden items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400 sm:flex">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Unsaved changes
              </span>
            )}
            <Button
              variant="contained"
              size="small"
              disabled={!project || !!cropping}
              title={cropping ? "Apply or cancel the crop first" : "Save the image"}
              onClick={() => setShowSave(true)}
            >
              Save…
            </Button>
          </div>
        </div>

        {error && (
          <div className="px-4 pt-3">
            <ErrorNote>{error}</ErrorNote>
          </div>
        )}

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          {/* Tool rail */}
          {project && (
            <nav
              aria-label="Add to the image"
              className="app-card flex shrink-0 gap-1 overflow-x-auto border-b p-1.5 shadow-none md:w-[80px] md:flex-col md:border-b-0 md:border-r md:py-3"
            >
              <RailButton icon={<TextFieldsIcon />} label="Text" disabled={!!cropping} onClick={() => addLayer(newTextLayer(project))} />
              <RailButton icon={<CropSquareIcon />} label="Banner" disabled={!!cropping} onClick={() => addLayer(newShapeLayer(project, "rect"))} />
              <RailButton icon={<CircleOutlinedIcon />} label="Circle" disabled={!!cropping} onClick={() => addLayer(newShapeLayer(project, "ellipse"))} />
              <RailButton icon={<AddPhotoAlternateOutlinedIcon />} label="Logo" disabled={!!cropping} title="Add a logo or picture — from the library or a new upload" onClick={() => setShowLogoPicker("logo")} />
              <RailButton icon={<QrCode2Icon />} label="QR code" disabled={!!cropping} title="Add a QR code that opens a website" onClick={() => setQrDialog({ qr: { ...QR_DEFAULTS } })} />
              <div className="app-divider mx-1 border-l md:mx-2 md:my-1 md:border-l-0 md:border-t" />
              <RailButton icon={<DashboardCustomizeOutlinedIcon />} label="Canvas" active={!selectedId && !cropping} disabled={!!cropping} title="Screen size and background" onClick={() => setSelectedId(null)} />
            </nav>
          )}

          {/* Stage */}
          <div className="relative order-1 flex min-h-[45vh] min-w-0 flex-1 flex-col md:order-none">
            <div
              ref={stageRef}
              className="flex min-h-0 flex-1 overflow-auto"
              style={{
                backgroundImage: `radial-gradient(${isDark ? "#1e293b" : "#cbd5e1"} 1px, transparent 1px)`,
                backgroundSize: "18px 18px",
              }}
              onPointerDown={(e) => e.target === e.currentTarget && !cropping && setSelectedId(null)}
              onWheel={(e) => {
                // Ctrl/⌘ + scroll zooms, like most editors.
                if (!(e.ctrlKey || e.metaKey) || !project) return;
                e.preventDefault();
                zoomBy(e.deltaY < 0 ? 1 : -1);
              }}
            >
              {!project ? (
                <div className="m-auto">
                  <Spinner size={32} label="Loading the image" />
                </div>
              ) : (
                <div className="relative m-auto shrink-0 p-6" onPointerDown={(e) => e.target === e.currentTarget && !cropping && setSelectedId(null)}>
                  <canvas
                    ref={canvasRef}
                    width={project.width}
                    height={project.height}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                    onDoubleClick={(e) => {
                      // Double-click a picture to crop it.
                      if (cropping) return;
                      const layer = hitLayer(toCanvas(e), canvasRef.current.getContext("2d"));
                      if (layer?.type === "image" && !layer.qr) startCrop(layer);
                    }}
                    className="block touch-none rounded shadow-2xl"
                    style={{ width: project.width * scale, height: project.height * scale }}
                  />
                  {selected && !cropping && selectionBox && (
                    <SelectionToolbar
                      box={selectionBox}
                      canvasWidth={project.width * scale}
                      layer={selected}
                      onDuplicate={duplicateSelected}
                      onForward={() => moveLayer(1)}
                      onBackward={() => moveLayer(-1)}
                      onCrop={() => startCrop()}
                      onEditQr={() => setQrDialog({ layerId: selected.id, qr: selected.qr })}
                      onDelete={removeSelected}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Hint + zoom */}
            {project && (
              <div className="app-card flex items-center gap-3 border-t px-3 py-1.5 shadow-none">
                <p className="app-muted min-w-0 flex-1 truncate text-xs">
                  {cropping
                    ? "Drag the edges or corners to crop · drag inside to move · Enter applies, Esc cancels"
                    : selected?.type === "text"
                      ? "Drag to move · corners resize the text · the right-edge handle changes where lines wrap"
                      : selected
                        ? "Drag to move (it snaps to the center) · corners resize · arrow keys nudge · Delete removes"
                        : "Click anything to edit it · double-click a photo to crop · Ctrl/⌘ + scroll to zoom"}
                </p>
                <div className="flex shrink-0 items-center gap-0.5">
                  <IconButton title="Zoom out (Ctrl/⌘ −)" onClick={() => zoomBy(-1)}>
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                  <button
                    type="button"
                    className="app-muted h-8 w-12 rounded-lg text-center text-xs font-semibold tabular-nums transition hover:bg-[color:var(--app-hover)] hover:text-[color:var(--app-text)]"
                    title="Actual size (100%)"
                    aria-label="Actual size (100%)"
                    onClick={() => setZoom(1)}
                  >
                    {Math.round(scale * 100)}%
                  </button>
                  <IconButton title="Zoom in (Ctrl/⌘ +)" onClick={() => zoomBy(1)}>
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                  <IconButton title="Fit to window (Ctrl/⌘ 0)" active={zoom === "fit"} onClick={() => setZoom("fit")}>
                    <FitScreenIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
            )}
          </div>

          {/* Properties */}
          <aside className="app-card order-2 max-h-[45vh] w-full shrink-0 overflow-y-auto border-t shadow-none md:order-none md:max-h-none md:w-80 md:border-l md:border-t-0">
            {!project ? null : cropping ? (
              <CropPanel
                cropping={cropping}
                project={project}
                layer={project.layers.find((l) => l.id === cropping.layerId)}
                image={images[project.layers.find((l) => l.id === cropping.layerId)?.src]}
                onAspect={setCropAspect}
                onReset={() => setCropping((c) => ({ ...c, aspect: null, rect: { ...c.full } }))}
                onApply={applyCrop}
                onCancel={() => setCropping(null)}
              />
            ) : (
              <>
                {selected ? (
                  <LayerPanel
                    layer={selected}
                    label={layerLabel(selected)}
                    update={updateSelected}
                    onFit={fitSelected}
                    onRotate={rotateSelected}
                    onFlip={flipSelected}
                    onCrop={() => startCrop()}
                    onEditQr={() => setQrDialog({ layerId: selected.id, qr: selected.qr })}
                    onDelete={removeSelected}
                    onDuplicate={duplicateSelected}
                    onForward={() => moveLayer(1)}
                    onBackward={() => moveLayer(-1)}
                    onCenter={(axis) => {
                      const b = layerBounds(canvasRef.current.getContext("2d"), selected);
                      updateSelected(
                        axis === "x"
                          ? { x: (project.width - b.w) / 2 }
                          : { y: (project.height - b.h) / 2 }
                      );
                    }}
                    onDeselect={() => setSelectedId(null)}
                  />
                ) : (
                  <>
                    <Section title="Screen">
                  <div className="grid grid-cols-3 gap-1.5">
                    {CANVAS_PRESETS.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => setPreset(p.key)}
                        aria-pressed={(project.preset || "landscape") === p.key}
                        className={`rounded-xl border px-2 py-2 text-center text-xs font-medium transition ${
                          (project.preset || "landscape") === p.key
                            ? "border-sky-500/60 bg-sky-500/15 text-sky-700 dark:text-sky-300"
                            : "app-input hover:border-sky-400"
                        }`}
                      >
                        <span
                          className={`mx-auto mb-1 block rounded-sm border-2 border-current ${
                            p.key === "portrait" ? "h-6 w-3.5" : p.key === "image" ? "h-4 w-5 border-dashed" : "h-3.5 w-6"
                          }`}
                        />
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <p className="app-muted text-xs">
                    TVs are 1920×1080. Saving shrinks oversized photos to what
                    the screen needs.
                  </p>
                </Section>
                    <Section title="Background">
                    <Segmented
                      value={project.background.type}
                      onChange={(type) =>
                        type === "image"
                          ? setShowLogoPicker("background")
                          : updateBackground({ type, src: "source" })
                      }
                      options={[
                        { value: "blur", label: "Blurred" },
                        { value: "color", label: "Color" },
                        { value: "gradient", label: "Gradient" },
                        { value: "image", label: "Image" },
                      ]}
                    />
                    {project.background.type === "blur" && (
                      <>
                        <RangeField
                          label="Blur"
                          value={project.background.blur ?? 40}
                          min={4}
                          max={120}
                          onChange={(v) => updateBackground({ blur: v }, "blur")}
                        />
                        <RangeField
                          label="Darken"
                          value={Math.round((project.background.dim ?? 0) * 100)}
                          min={0}
                          max={80}
                          format={(v) => `${v}%`}
                          onChange={(v) => updateBackground({ dim: v / 100 }, "dim")}
                        />
                      </>
                    )}
                    {project.background.type === "color" && (
                      <ColorInput
                        value={project.background.color}
                        onChange={(color) => updateBackground({ color }, "color")}
                      />
                    )}
                    {project.background.type === "gradient" && (
                      <>
                        <div className="flex gap-3">
                          <ColorInput
                            value={project.background.color}
                            onChange={(color) => updateBackground({ color }, "color")}
                          />
                          <ColorInput
                            value={project.background.color2}
                            onChange={(color2) => updateBackground({ color2 }, "color2")}
                          />
                        </div>
                        <RangeField
                          label="Angle"
                          value={project.background.angle ?? 135}
                          min={0}
                          max={360}
                          format={(v) => `${v}°`}
                          onChange={(v) => updateBackground({ angle: v }, "angle")}
                        />
                      </>
                    )}
                    {project.background.type === "image" && (
                      <>
                        <Button size="small" variant="outlined" onClick={() => setShowLogoPicker("background")}>
                          Choose another image
                        </Button>
                        <RangeField
                          label="Darken"
                          value={Math.round((project.background.dim ?? 0) * 100)}
                          min={0}
                          max={80}
                          format={(v) => `${v}%`}
                          onChange={(v) => updateBackground({ dim: v / 100 }, "dim")}
                        />
                      </>
                    )}
                    <p className="app-muted text-xs">
                      Fills any part of the screen the photo doesn't cover.
                      Click something on the canvas to edit it.
                    </p>
                  </Section>
                  </>
                )}

                <Section title="Layers" aside={<span className="app-muted text-xs">top to bottom</span>}>
                  <div className="space-y-0.5">
                    {[...project.layers].reverse().map((l) => (
                      <div
                        key={l.id}
                        className={`group flex items-center rounded-lg transition ${
                          l.id === selectedId
                            ? "bg-sky-500/15 text-sky-800 dark:text-sky-200"
                            : "hover:bg-[color:var(--app-hover)]"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => setSelectedId(l.id)}
                          aria-current={l.id === selectedId ? "true" : undefined}
                          className={`flex min-w-0 flex-1 items-center gap-2 px-2 py-2 text-left text-xs font-medium ${l.hidden ? "opacity-50" : ""}`}
                        >
                          {l.qr ? (
                            <QrCode2Icon sx={{ fontSize: 15 }} />
                          ) : l.type === "text" ? (
                            <TextFieldsIcon sx={{ fontSize: 15 }} />
                          ) : l.type === "shape" ? (
                            l.shape === "ellipse" ? <CircleOutlinedIcon sx={{ fontSize: 15 }} /> : <CropSquareIcon sx={{ fontSize: 15 }} />
                          ) : (
                            <ImageOutlinedIcon sx={{ fontSize: 15 }} />
                          )}
                          <span className="truncate">{layerLabel(l)}</span>
                        </button>
                        <button
                          type="button"
                          title={l.hidden ? "Show (hidden layers aren't saved in the image)" : "Hide"}
                          aria-label={l.hidden ? `Show ${layerLabel(l)}` : `Hide ${layerLabel(l)}`}
                          onClick={() => updateLayer(l.id, { hidden: !l.hidden })}
                          className={`mr-1 rounded-md p-1 transition focus-visible:opacity-100 ${
                            l.hidden
                              ? ""
                              : "opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
                          } ${l.id === selectedId ? "" : "app-muted"}`}
                        >
                          {l.hidden ? (
                            <VisibilityOffOutlinedIcon sx={{ fontSize: 15 }} />
                          ) : (
                            <VisibilityOutlinedIcon sx={{ fontSize: 15 }} />
                          )}
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setSelectedId(null)}
                      aria-current={!selectedId ? "true" : undefined}
                      className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-xs font-medium transition ${
                        !selectedId
                          ? "bg-sky-500/15 text-sky-800 dark:text-sky-200"
                          : "hover:bg-[color:var(--app-hover)]"
                      }`}
                    >
                      <span className="inline-block h-3.5 w-3.5 rounded-sm border border-current" />
                      Background
                    </button>
                  </div>
                </Section>
              </>
            )}
          </aside>
        </div>
      </div>

      {qrDialog && (
        <QrDialog
          initial={qrDialog.qr}
          editing={!!qrDialog.layerId}
          userId={userId}
          onClose={() => setQrDialog(null)}
          onSave={(qr) => saveQr(qr, qrDialog.layerId)}
        />
      )}

      {showLogoPicker && (
        <LibraryImagePicker
          images={otherImages}
          scope={scope}
          userId={userId}
          onUploaded={(uploaded) => {
            setUploadedImages((list) => [...list, uploaded]);
            onLibraryChanged?.();
          }}
          title={showLogoPicker === "background" ? "Choose a background image" : "Add a logo or image"}
          onClose={() => setShowLogoPicker(false)}
          onPick={showLogoPicker === "background" ? setBackgroundImage : addLogo}
        />
      )}

      {showSave && project && (
        <SaveDialog
          asset={asset}
          scope={scope}
          project={project}
          images={images}
          libraryNames={(libraryImages || []).map((a) => a.filename)}
          userId={userId}
          onClose={() => setShowSave(false)}
          onSaved={(saved) => {
            setShowSave(false);
            onSaved(saved);
          }}
        />
      )}
    </Dialog>
  );
};

// Tool rail button: icon over a short label.
const RailButton = ({ icon, label, title, onClick, disabled, active }) => (
  <button
    type="button"
    title={title || `Add ${label.toLowerCase()}`}
    onClick={onClick}
    disabled={disabled}
    aria-pressed={active === undefined ? undefined : !!active}
    className={`flex min-w-[60px] flex-col items-center gap-1 rounded-xl px-1.5 py-2 text-[11px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 ${
      active
        ? "bg-sky-500/15 text-sky-700 dark:text-sky-300"
        : "app-muted hover:bg-[color:var(--app-hover)] hover:text-[color:var(--app-text)]"
    }`}
  >
    {icon}
    {label}
  </button>
);

// Quick actions floating just above (or below, near the top edge) the
// selected layer.
const SelectionToolbar = ({ box, canvasWidth, layer, onDuplicate, onForward, onBackward, onCrop, onEditQr, onDelete }) => {
  const width = 200;
  const above = box.y > 52;
  const style = {
    left: 24 + Math.min(Math.max(0, box.x + box.w / 2 - width / 2), Math.max(0, canvasWidth - width)),
    top: 24 + (above ? box.y - 46 : box.y + box.h + 10),
  };
  const Btn = ({ title, onClick, children, danger }) => (
    <button
      type="button"
      title={title}
      aria-label={title}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={onClick}
      className={`rounded-lg p-1.5 transition ${
        danger
          ? "text-slate-300 hover:bg-red-500/80 hover:text-white"
          : "text-slate-200 hover:bg-white/15 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
  return (
    <div
      className="absolute z-10 flex items-center gap-0.5 rounded-xl bg-slate-900/90 px-1 py-0.5 shadow-lg ring-1 ring-white/10"
      style={style}
    >
      <Btn title="Duplicate (Ctrl/⌘+D)" onClick={onDuplicate}>
        <ContentCopyIcon sx={{ fontSize: 17 }} />
      </Btn>
      <Btn title="Bring forward" onClick={onForward}>
        <FlipToFrontOutlinedIcon sx={{ fontSize: 17 }} />
      </Btn>
      <Btn title="Send back" onClick={onBackward}>
        <FlipToBackOutlinedIcon sx={{ fontSize: 17 }} />
      </Btn>
      {layer.type === "image" && !layer.qr && (
        <Btn title="Crop" onClick={onCrop}>
          <CropIcon sx={{ fontSize: 17 }} />
        </Btn>
      )}
      {layer.qr && (
        <Btn title="Edit QR code" onClick={onEditQr}>
          <QrCode2Icon sx={{ fontSize: 17 }} />
        </Btn>
      )}
      <span className="mx-0.5 h-5 w-px bg-white/15" />
      <Btn title="Delete (Delete key)" onClick={onDelete} danger>
        <DeleteOutlineIcon sx={{ fontSize: 17 }} />
      </Btn>
    </div>
  );
};

// Resize handles for a layer's bounds; text also gets a right-edge handle to
// change its wrap width without changing the font size.
// Crop box handles: corners, plus edges when the shape is free.
const cropHandles = (R, aspect) => {
  const pts = [
    { key: "nw", x: R.x, y: R.y, cursor: "nwse-resize" },
    { key: "ne", x: R.x + R.w, y: R.y, cursor: "nesw-resize" },
    { key: "sw", x: R.x, y: R.y + R.h, cursor: "nesw-resize" },
    { key: "se", x: R.x + R.w, y: R.y + R.h, cursor: "nwse-resize" },
  ];
  if (!aspect) {
    pts.push(
      { key: "n", x: R.x + R.w / 2, y: R.y, cursor: "ns-resize" },
      { key: "s", x: R.x + R.w / 2, y: R.y + R.h, cursor: "ns-resize" },
      { key: "w", x: R.x, y: R.y + R.h / 2, cursor: "ew-resize" },
      { key: "e", x: R.x + R.w, y: R.y + R.h / 2, cursor: "ew-resize" }
    );
  }
  return pts;
};

/**
 * New crop box for a drag: move it, or pull an edge/corner — always inside
 * the image (F), never smaller than `min`, keeping `aspect` (w/h) when set.
 */
const dragCropRect = (d, pt, F, aspect, min) => {
  const R = d.rect;
  const dx = pt.x - d.start.x;
  const dy = pt.y - d.start.y;
  const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), Math.max(lo, hi));
  if (d.mode === "crop-move") {
    return {
      ...R,
      x: clamp(R.x + dx, F.x, F.x + F.w - R.w),
      y: clamp(R.y + dy, F.y, F.y + F.h - R.h),
    };
  }
  const k = d.handle;
  let left = R.x;
  let right = R.x + R.w;
  let top = R.y;
  let bottom = R.y + R.h;
  if (k.includes("w")) left = clamp(left + dx, F.x, right - min);
  if (k.includes("e")) right = clamp(right + dx, left + min, F.x + F.w);
  if (k.includes("n")) top = clamp(top + dy, F.y, bottom - min);
  if (k.includes("s")) bottom = clamp(bottom + dy, top + min, F.y + F.h);
  if (!aspect) return { x: left, y: top, w: right - left, h: bottom - top };

  // Locked shape (corners only): width leads, height follows; shrink both if
  // the height would run off the image.
  const north = k.includes("n");
  const west = k.includes("w");
  let w = right - left;
  let h = w / aspect;
  const anchorY = north ? R.y + R.h : R.y;
  const room = north ? anchorY - F.y : F.y + F.h - anchorY;
  if (h > room) {
    h = room;
    w = h * aspect;
  }
  const x = west ? R.x + R.w - w : R.x;
  const y = north ? anchorY - h : anchorY;
  return { x, y, w, h };
};

const handlePoints = (layer, b) => {
  const pts = [
    { key: "nw", x: b.x, y: b.y, cursor: "nwse-resize" },
    { key: "ne", x: b.x + b.w, y: b.y, cursor: "nesw-resize" },
    { key: "sw", x: b.x, y: b.y + b.h, cursor: "nesw-resize" },
    { key: "se", x: b.x + b.w, y: b.y + b.h, cursor: "nwse-resize" },
  ];
  if (layer.type === "text") {
    pts.push({ key: "e", x: b.x + b.w, y: b.y + b.h / 2, cursor: "ew-resize" });
  }
  return pts;
};

// ================= crop tool controls =================

const CropPanel = ({ cropping, project, layer, image, onAspect, onReset, onApply, onCancel }) => {
  const original = image && layer ? croppedSize(image, { ...layer, crop: undefined }) : null;
  const options = [
    { label: "Free", value: null },
    ...(original ? [{ label: "Original", value: original.w / original.h }] : []),
    { label: "Screen", value: project.width / project.height },
    { label: "16:9", value: 16 / 9 },
    { label: "4:3", value: 4 / 3 },
    { label: "1:1", value: 1 },
    { label: "9:16", value: 9 / 16 },
  ];
  const same = (a, b) => (a == null ? b == null : b != null && Math.abs(a - b) < 0.001);
  const pct = (v) => Math.round(v * 100);
  return (
    <Section title="Crop">
      <p className="app-muted text-xs">
        Drag the edges or corners to choose the part to keep, and drag inside
        the box to move it. The dimmed part is cut off.
      </p>
      <Field label="Shape">
        <div className="mt-1 grid grid-cols-4 gap-1">
          {options.map((o) => (
            <button
              key={o.label}
              type="button"
              onClick={() => onAspect(o.value)}
              aria-pressed={same(cropping.aspect, o.value)}
              className={`rounded-lg border px-1.5 py-1 text-xs font-medium transition ${
                same(cropping.aspect, o.value)
                  ? "border-sky-500/60 bg-sky-500/15 text-sky-700 dark:text-sky-300"
                  : "app-input hover:border-sky-400"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </Field>
      <p className="app-muted text-xs tabular-nums">
        Keeping {pct(cropping.rect.w / cropping.full.w)}% × {pct(cropping.rect.h / cropping.full.h)}% of the picture
      </p>
      <div className="flex gap-2">
        <Button variant="contained" size="small" onClick={onApply}>
          Apply crop
        </Button>
        <Button size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="small" className="!ml-auto" onClick={onReset}>
          Reset
        </Button>
      </div>
      <p className="app-muted text-xs">Enter applies · Esc cancels</p>
    </Section>
  );
};

// ================= selected-layer controls =================

const LayerPanel = ({
  layer,
  label,
  update,
  onFit,
  onRotate,
  onFlip,
  onCrop,
  onEditQr,
  onDelete,
  onDuplicate,
  onForward,
  onBackward,
  onCenter,
  onDeselect,
}) => (
  <>
    <Section
      title={label}
      aside={
        <button type="button" className="app-link text-xs" onClick={onDeselect}>
          Done
        </button>
      }
    >
      {layer.type === "text" && (
        <>
          <textarea
            aria-label="Text"
            className={`${INPUT} min-h-[72px]`}
            value={layer.text}
            onChange={(e) => update({ text: e.target.value }, "text")}
            placeholder="Type your text"
          />
          <div className="flex gap-2">
            <select
              aria-label="Font"
              className={`${INPUT} flex-1`}
              value={layer.fontFamily}
              onChange={(e) => {
                update({ fontFamily: e.target.value });
                document.fonts?.load(`700 40px "${e.target.value}"`);
              }}
              style={{ fontFamily: layer.fontFamily }}
            >
              {FONTS.map((f) => (
                <option key={f.family} value={f.family} style={{ fontFamily: f.family }}>
                  {f.family}
                </option>
              ))}
            </select>
            <input
              className={`${INPUT} w-20`}
              type="number"
              min="6"
              max="600"
              title="Font size (px)"
              aria-label="Font size (px)"
              value={Math.round(layer.fontSize)}
              onChange={(e) => update({ fontSize: Math.max(6, Number(e.target.value) || 6) }, "fontSize")}
            />
          </div>
          <div className="flex flex-wrap gap-1">
            <IconButton title="Bold" active={layer.bold} onClick={() => update({ bold: !layer.bold })}>
              <FormatBoldIcon fontSize="small" />
            </IconButton>
            <IconButton title="Italic" active={layer.italic} onClick={() => update({ italic: !layer.italic })}>
              <FormatItalicIcon fontSize="small" />
            </IconButton>
            <span className="w-2" />
            {[
              ["left", <FormatAlignLeftIcon fontSize="small" />],
              ["center", <FormatAlignCenterIcon fontSize="small" />],
              ["right", <FormatAlignRightIcon fontSize="small" />],
            ].map(([align, icon]) => (
              <IconButton key={align} title={`Align ${align}`} active={layer.align === align} onClick={() => update({ align })}>
                {icon}
              </IconButton>
            ))}
          </div>
          <Field label="Color">
            <ColorInput value={layer.color} onChange={(color) => update({ color }, "color")} />
          </Field>
          <RangeField
            label="Line spacing"
            value={layer.lineHeight ?? 1.15}
            min={0.8}
            max={2}
            step={0.05}
            format={(v) => v.toFixed(2)}
            onChange={(v) => update({ lineHeight: v }, "lineHeight")}
          />
          <label className="flex items-center justify-between text-sm">
            Drop shadow
            <Switch size="small" checked={!!layer.shadow} onChange={(e) => update({ shadow: e.target.checked })} />
          </label>
          <Field label="Outline">
            <div className="flex items-center gap-3">
              <ColorInput value={layer.outlineColor} onChange={(outlineColor) => update({ outlineColor }, "outlineColor")} />
              <input
                className={`${INPUT} w-20`}
                type="number"
                min="0"
                max="40"
                title="Outline width (px)"
                aria-label="Outline width (px)"
                value={Math.round(layer.outlineWidth || 0)}
                onChange={(e) => update({ outlineWidth: Math.max(0, Number(e.target.value) || 0) }, "outlineWidth")}
              />
            </div>
          </Field>
          <Field label="Background band">
            <ColorInput
              value={layer.bgColor}
              allowNone
              noneLabel="none — pick a color to add one"
              onChange={(bgColor) => update({ bgColor }, "bgColor")}
            />
          </Field>
          {layer.bgColor && (
            <>
              <RangeField
                label="Band opacity"
                value={Math.round((layer.bgOpacity ?? 1) * 100)}
                min={5}
                max={100}
                format={(v) => `${v}%`}
                onChange={(v) => update({ bgOpacity: v / 100 }, "bgOpacity")}
              />
              <RangeField
                label="Band padding"
                value={Math.round(layer.bgPadding ?? 0)}
                min={0}
                max={Math.max(120, Math.round(layer.fontSize))}
                onChange={(v) => update({ bgPadding: v }, "bgPadding")}
              />
              <RangeField
                label="Band corners"
                value={Math.round(layer.bgRadius ?? 0)}
                min={0}
                max={Math.max(80, Math.round(layer.fontSize))}
                onChange={(v) => update({ bgRadius: v }, "bgRadius")}
              />
            </>
          )}
          <p className="app-muted text-xs">
            Drag a corner to resize the text; drag the right-edge handle to
            change where lines wrap.
          </p>
        </>
      )}

      {layer.qr && (
        <>
          <p className="break-all text-sm" title={layer.qr.data}>
            {layer.qr.data}
          </p>
          <Button size="small" variant="outlined" startIcon={<QrCode2Icon />} onClick={onEditQr}>
            Edit QR code…
          </Button>
          <p className="app-muted text-xs">
            Drag a corner to resize. Keep it big — about a quarter of the
            screen height — so people can scan it from a few feet away.
          </p>
        </>
      )}

      {layer.type === "image" && !layer.qr && (
        <>
          <div className="grid grid-cols-2 gap-1.5">
            <Button size="small" variant="outlined" onClick={() => onFit("contain")} title="Show the whole image">
              Fit to screen
            </Button>
            <Button size="small" variant="outlined" onClick={() => onFit("cover")} title="Cover the whole screen (edges may be cut off)">
              Fill screen
            </Button>
          </div>
          <div className="flex gap-1">
            <IconButton title="Rotate left" onClick={() => onRotate(-1)}>
              <RotateLeftIcon fontSize="small" />
            </IconButton>
            <IconButton title="Rotate right" onClick={() => onRotate(1)}>
              <RotateRightIcon fontSize="small" />
            </IconButton>
            <IconButton title="Crop (or double-click the picture)" active={!!layer.crop} onClick={onCrop}>
              <CropIcon fontSize="small" />
              <span className="ml-1 text-xs">Crop</span>
            </IconButton>
            <IconButton title="Mirror" active={layer.flipX} onClick={onFlip}>
              <FlipIcon fontSize="small" />
            </IconButton>
          </div>
          <RangeField label="Brightness" value={layer.brightness ?? 100} min={0} max={200} format={(v) => `${v}%`} onChange={(v) => update({ brightness: v }, "brightness")} />
          <RangeField label="Contrast" value={layer.contrast ?? 100} min={0} max={200} format={(v) => `${v}%`} onChange={(v) => update({ contrast: v }, "contrast")} />
          <RangeField label="Saturation" value={layer.saturation ?? 100} min={0} max={200} format={(v) => `${v}%`} onChange={(v) => update({ saturation: v }, "saturation")} />
          <RangeField label="Rounded corners" value={Math.round(layer.radius || 0)} min={0} max={300} onChange={(v) => update({ radius: v }, "radius")} />
          {(layer.brightness !== 100 || layer.contrast !== 100 || layer.saturation !== 100) && (
            <button
              type="button"
              className="app-link text-xs"
              onClick={() => update({ brightness: 100, contrast: 100, saturation: 100 })}
            >
              Reset colors
            </button>
          )}
        </>
      )}

      {layer.type === "shape" && (
        <>
          <Field label="Color">
            <ColorInput value={layer.color} onChange={(color) => update({ color }, "color")} />
          </Field>
          {layer.shape !== "ellipse" && (
            <RangeField label="Rounded corners" value={Math.round(layer.radius || 0)} min={0} max={300} onChange={(v) => update({ radius: v }, "radius")} />
          )}
          <p className="app-muted text-xs">
            Drag the corners to stretch it (hold Shift to keep its shape).
            Put it under text with Send back / Bring forward.
          </p>
        </>
      )}

      <RangeField
        label="Opacity"
        value={Math.round((layer.opacity ?? 1) * 100)}
        min={5}
        max={100}
        format={(v) => `${v}%`}
        onChange={(v) => update({ opacity: v / 100 }, "opacity")}
      />
    </Section>

    <Section title="Arrange">
      <div className="grid grid-cols-2 gap-1.5">
        <Button size="small" onClick={() => onCenter("x")}>Center across</Button>
        <Button size="small" onClick={() => onCenter("y")}>Center up/down</Button>
        <Button size="small" startIcon={<ArrowUpwardIcon />} onClick={onForward}>Bring forward</Button>
        <Button size="small" startIcon={<ArrowDownwardIcon />} onClick={onBackward}>Send back</Button>
        <Button size="small" startIcon={<ContentCopyIcon />} onClick={onDuplicate}>Duplicate</Button>
        <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={onDelete}>Delete</Button>
      </div>
      <p className="app-muted text-xs">
        Arrow keys nudge (Shift = 10 px) · Delete removes · Ctrl/⌘+D duplicates
      </p>
    </Section>
  </>
);

// ================= pickers & save =================

const QrDialog = ({ initial, editing, userId, onClose, onSave }) => {
  const [data, setData] = useState(initial.data || "");
  const [dark, setDark] = useState(initial.dark || "#000000");
  const [light, setLight] = useState(initial.light || "#ffffff");
  const [margin, setMargin] = useState(initial.margin ?? 2);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const target = normalizeQrData(data);
  const transparent = light === "transparent";

  // Live preview, a moment after typing stops.
  useEffect(() => {
    if (!target) {
      setPreview(null);
      return undefined;
    }
    let url = null;
    let cancelled = false;
    const timer = setTimeout(() => {
      fetchSignageQrBlobUrl({ data: target, dark, light, margin }, userId)
        .then((u) => {
          if (cancelled) return URL.revokeObjectURL(u);
          url = u;
          setPreview(u);
          setError(null);
        })
        .catch((err) => !cancelled && setError(err.message));
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (url) URL.revokeObjectURL(url);
    };
  }, [target, dark, light, margin, userId]);

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ data: target, dark, light, margin });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <div className="space-y-4 p-6">
        <h3 className="flex items-center gap-3 text-lg font-semibold tracking-tight">
          <span className="app-icon-tile h-10 w-10 rounded-xl bg-violet-500/15 text-violet-600 dark:text-violet-300">
            <QrCode2Icon fontSize="small" />
          </span>
          {editing ? "Edit QR code" : "Add a QR code"}
        </h3>
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex-1 space-y-3">
            <label className="block">
              <span className="app-muted text-xs font-medium">Website address</span>
              <input
                className={INPUT}
                value={data}
                autoFocus
                placeholder="bigclumber.com/spring-sale"
                onChange={(e) => setData(e.target.value)}
              />
            </label>
            {target && target !== data.trim() && (
              <p className="app-muted -mt-2 text-xs">Opens {target}</p>
            )}
            <Field label="Code color">
              <ColorInput value={dark} onChange={setDark} />
            </Field>
            <label className="flex items-center justify-between text-sm">
              Transparent background
              <Switch
                size="small"
                checked={transparent}
                onChange={(e) => setLight(e.target.checked ? "transparent" : "#ffffff")}
              />
            </label>
            {!transparent && (
              <Field label="Background color">
                <ColorInput value={light} onChange={setLight} />
              </Field>
            )}
            <RangeField label="Border" value={margin} min={0} max={8} onChange={setMargin} />
            <p className="app-muted text-xs">
              Dark code on a light background scans best. Test it with a phone
              before it goes on the TVs.
            </p>
          </div>
          <div
            className="flex h-48 w-48 shrink-0 items-center justify-center self-center rounded-xl border border-[color:var(--app-input-border)]"
            style={{
              backgroundImage: transparent
                ? "repeating-conic-gradient(#cbd5e1 0% 25%, #f8fafc 0% 50%)"
                : undefined,
              backgroundSize: "16px 16px",
            }}
          >
            {preview ? (
              <img src={preview} alt="QR code preview" className="h-44 w-44 object-contain" />
            ) : (
              <span className="app-muted px-4 text-center text-xs">
                Type an address to see the code
              </span>
            )}
          </div>
        </div>
        <ErrorNote>{error}</ErrorNote>
        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={!target || saving} onClick={save}>
            {editing ? "Update" : "Add to image"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

// Checkerboard behind thumbnails so transparent PNG logos read as such.
const CHECKER = {
  backgroundImage:
    "linear-gradient(45deg, rgba(148,163,184,.25) 25%, transparent 25%), linear-gradient(-45deg, rgba(148,163,184,.25) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(148,163,184,.25) 75%), linear-gradient(-45deg, transparent 75%, rgba(148,163,184,.25) 75%)",
  backgroundSize: "14px 14px",
  backgroundPosition: "0 0, 0 7px, 7px -7px, -7px 0",
};

// Pick a library image (logo layer / background) or upload a new one, which
// goes into the same library and is used right away.
const LibraryImagePicker = ({ images, scope, userId, title, onClose, onPick, onUploaded }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragDepth, setDragDepth] = useState(0);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const upload = async (files) => {
    const file = files?.[0];
    if (!file || uploading) return;
    if (!file.type.startsWith("image/")) {
      setError("Pick an image file (jpg, png, gif, bmp, or webp).");
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const uploaded = await uploadSignageAsset(scope.id, file, userId);
      onUploaded(uploaded);
      onPick(uploaded);
    } catch (err) {
      setError(err.message);
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
  const query = search.trim().toLowerCase();
  const shown = [...images]
    .filter((a) => !query || a.filename.toLowerCase().includes(query))
    .sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
  const libraryName = scope.company ? "the company library" : `${scope.name}'s library`;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <div
        className="relative flex max-h-[88vh] flex-col"
        onDragEnter={(e) => {
          if (!isFileDrag(e)) return;
          e.preventDefault();
          setDragDepth((d) => d + 1);
        }}
        onDragOver={(e) => isFileDrag(e) && e.preventDefault()}
        onDragLeave={(e) => isFileDrag(e) && setDragDepth((d) => Math.max(0, d - 1))}
        onDrop={(e) => {
          if (!isFileDrag(e)) return;
          e.preventDefault();
          setDragDepth(0);
          upload(e.dataTransfer.files);
        }}
      >
        {dragDepth > 0 && (
          <div className="pointer-events-none absolute inset-2 z-20 flex items-center justify-center rounded-2xl border-2 border-dashed border-sky-500 bg-sky-50/90 dark:bg-sky-950/85">
            <p className="text-lg font-semibold">Drop to upload and use it</p>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept={IMAGE_FILES}
          className="hidden"
          onChange={(e) => upload(e.target.files)}
        />

        <div className="app-divider flex items-center gap-3 border-b px-5 py-4">
          <span className="app-icon-tile h-10 w-10 rounded-xl bg-violet-500/15 text-violet-600 dark:text-violet-300">
            <AddPhotoAlternateOutlinedIcon fontSize="small" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold tracking-tight">{title}</h3>
            <p className="app-muted text-xs">
              Pick from {libraryName}, or upload a new image — it's added to the
              library too.
            </p>
          </div>
          <button
            type="button"
            className="app-icon-btn -mr-2"
            title="Close"
            aria-label="Close"
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {images.length > 8 && (
            <input
              className={INPUT}
              placeholder="Search by file name"
              aria-label="Search by file name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
          <ErrorNote>{error}</ErrorNote>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className="app-divider flex aspect-[4/3] flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed text-center transition hover:border-sky-400 hover:bg-sky-500/5 disabled:opacity-60"
            >
              {uploading ? (
                <>
                  <CircularProgress size={22} />
                  <span className="text-sm font-medium">Uploading…</span>
                </>
              ) : (
                <>
                  <AddPhotoAlternateOutlinedIcon className="app-accent" sx={{ fontSize: 30 }} />
                  <span className="text-sm font-medium">Upload new</span>
                  <span className="app-muted px-2 text-[11px]">or drop a file anywhere here</span>
                </>
              )}
            </button>
            {shown.map((a) => (
              <PickerCard key={a.id} asset={a} userId={userId} disabled={uploading} onPick={() => onPick(a)} />
            ))}
          </div>
          {images.length === 0 && (
            <p className="app-muted text-sm">
              No other images in this library yet — upload one to use it.
            </p>
          )}
          {images.length > 0 && shown.length === 0 && (
            <p className="app-muted text-sm">No images match “{search}”.</p>
          )}
        </div>

        <div className="app-divider flex items-center gap-3 border-t px-5 py-3">
          <p className="app-muted min-w-0 flex-1 text-xs">
            Tip: PNG logos with a transparent background look best — the
            checkerboard shows where an image is see-through.
          </p>
          <Button onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </Dialog>
  );
};

const PickerCard = ({ asset, userId, disabled, onPick }) => {
  const [dims, setDims] = useState(null);
  const ext = (asset.filename.split(".").pop() || "").toLowerCase();
  const maybeTransparent = ["png", "webp", "gif"].includes(ext);
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onPick}
      className="app-card group overflow-hidden rounded-2xl border text-left transition hover:border-sky-500 hover:shadow-md disabled:opacity-60"
      title={`Use ${asset.filename}`}
    >
      <div className="relative">
        <LibraryThumb asset={asset} userId={userId} onDims={setDims} />
        {maybeTransparent && (
          <span className="absolute right-2 top-2 rounded-md bg-black/65 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white">
            {ext}
          </span>
        )}
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-sky-600/0 text-sm font-semibold text-white opacity-0 transition group-hover:bg-sky-600/40 group-hover:opacity-100">
          Use this
        </span>
      </div>
      <div className="px-2.5 py-2">
        <p className="truncate text-xs font-medium">{asset.filename}</p>
        <p className="app-muted text-[11px] tabular-nums">
          {dims ? `${dims.w}×${dims.h}` : "…"} · {formatBytes(asset.sizeBytes)}
        </p>
      </div>
    </button>
  );
};

const LibraryThumb = ({ asset, userId, onDims }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    fetchSignageAssetBlobUrl(asset.id, userId)
      .then((u) => {
        if (cancelled) return URL.revokeObjectURL(u);
        revoke = u;
        setUrl(u);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [asset.id, userId]);
  return (
    <div className="flex aspect-[4/3] items-center justify-center" style={CHECKER}>
      {url && (
        <img
          src={url}
          alt=""
          className="h-full w-full object-contain p-1.5"
          onLoad={(e) => onDims?.({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
        />
      )}
    </div>
  );
};

const renderToFile = (project, images, name) =>
  new Promise((resolve, reject) => {
    const canvas = env.createCanvas(project.width, project.height);
    renderProject(canvas.getContext("2d"), project, images, env);
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(new File([blob], `${name}.jpg`, { type: "image/jpeg" }))
          : reject(new Error("Couldn't create the image")),
      "image/jpeg",
      0.9
    );
  });

const SaveDialog = ({ asset, scope, project, images, libraryNames, userId, onClose, onSaved }) => {
  const [mode, setMode] = useState("replace");
  const suggested = useMemo(() => {
    const stem = stemOf(asset.filename);
    const taken = new Set(libraryNames.map((n) => stemOf(n).toLowerCase()));
    let candidate = `${stem}-edited`;
    for (let n = 2; taken.has(candidate.toLowerCase()); n++) candidate = `${stem}-edited-${n}`;
    return candidate;
  }, [asset.filename, libraryNames]);
  const [name, setName] = useState(suggested);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (mode === "replace") {
        const file = await renderToFile(project, images, stemOf(asset.filename));
        onSaved(await replaceSignageAssetContent(asset.id, file, project, userId));
      } else {
        const file = await renderToFile(project, images, name.trim() || suggested);
        onSaved(
          await uploadSignageAsset(scope.id, file, userId, {
            project,
            sourceFrom: asset.id,
          })
        );
      }
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const Option = ({ value, title, children }) => (
    <label
      className={`flex cursor-pointer gap-3 rounded-xl border p-3 transition ${
        mode === value ? "border-sky-500 bg-sky-500/10" : "app-divider hover:border-sky-400"
      }`}
    >
      <input type="radio" className="mt-1" checked={mode === value} onChange={() => setMode(value)} />
      <span>
        <span className="block text-sm font-semibold">{title}</span>
        <span className="app-muted block text-xs">{children}</span>
      </span>
    </label>
  );

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <div className="space-y-4 p-6">
        <h3 className="text-lg font-semibold tracking-tight">Save image</h3>
        <div className="space-y-2">
          <Option value="replace" title="Replace the original">
            Every playlist using “{asset.filename}” shows the new version —
            displays pick it up on their next check-in.
          </Option>
          <Option value="copy" title="Save as a new copy">
            Keeps the original. The copy is added to the library for you to
            put in playlists.
          </Option>
        </div>
        {mode === "copy" && (
          <label className="block">
            <span className="app-muted text-xs font-medium">Name</span>
            <div className="mt-1 flex items-center gap-1.5">
              <input className={INPUT} value={name} onChange={(e) => setName(e.target.value)} />
              <span className="app-muted text-sm">.jpg</span>
            </div>
          </label>
        )}
        <p className="app-muted text-xs">
          Saved as a {project.width}×{project.height} JPEG. Your text and layers
          are kept, so you can come back and edit them.
        </p>
        <ErrorNote>{error}</ErrorNote>
        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={saving} onClick={save}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default SignageImageEditor;
